import React, { useState, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, Transition } from '@headlessui/react';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ClipboardDocumentListIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import {
  integrationModalField,
  integrationSecondaryButton,
  integrationToolbarPrimaryButton,
} from '../../constants/integrationLogsUi';

interface Customer {
  id: string;
  accountName: string;
}

interface Engagement {
  id: string;
  name: string;
  number: string;
}

interface ServiceNowConfig {
  id: string;
  customerId: string;
  isEnabled: boolean;
  instanceUrl: string;
  authType: 'basic' | 'oauth';
  authUsername?: string;
  serviceNowCompanySysId?: string;
  defaultEngagementId?: string;
  syncIncidents: boolean;
  syncChanges: boolean;
  syncServiceRequests: boolean;
  pollingEnabled: boolean;
  pollingIntervalMinutes: number;
  pollFromDate?: string | null;
  updatedAt: string;
  Customer?: { id: string; accountName: string };
  Engagement?: Engagement | null;
}

export interface ServiceNowIntegrationConfigProps {
  embedded?: boolean;
  customerId?: string;
  engagementId?: string;
  customerName?: string;
  engagementLabel?: string;
}

function todayDateInputValue(): string {
  return new Date().toISOString().slice(0, 10);
}

function toDateInputValue(iso?: string | null): string {
  if (!iso) return todayDateInputValue();
  return iso.slice(0, 10);
}

function formatPollFromDateDisplay(iso?: string | null): string {
  if (!iso) return '—';
  const datePart = iso.slice(0, 10);
  const [year, month, day] = datePart.split('-');
  if (!year || !month || !day) return datePart;
  return `${month}/${day}/${year}`;
}

function formatEngagementLabel(engagement?: Engagement | null): string {
  if (!engagement) return '—';
  return `${engagement.number} - ${engagement.name}`;
}

const emptyForm = {
  customerId: '',
  isEnabled: false,
  instanceUrl: '',
  authType: 'basic' as 'basic' | 'oauth',
  authUsername: '',
  password: '',
  clientId: '',
  clientSecret: '',
  serviceNowCompanySysId: '',
  defaultEngagementId: '',
  syncIncidents: true,
  syncChanges: true,
  syncServiceRequests: true,
  pollingEnabled: true,
  pollingIntervalMinutes: 5,
  pollFromDate: todayDateInputValue(),
};

const ServiceNowIntegrationConfig: React.FC<ServiceNowIntegrationConfigProps> = ({
  embedded = false,
  customerId: lockedCustomerId,
  engagementId: lockedEngagementId,
  customerName,
  engagementLabel,
}) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(emptyForm);
  const [editingCustomerId, setEditingCustomerId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<ServiceNowConfig | null>(null);
  const [deleting, setDeleting] = useState(false);

  const { data: configsData, isLoading: listLoading, isError: listError } = useQuery({
    queryKey: ['servicenow-configs'],
    enabled: !embedded,
    queryFn: async () => {
      const res = await api.get('/integrations/servicenow/config');
      return res.data.data as ServiceNowConfig[];
    },
    refetchOnWindowFocus: false,
    staleTime: 30000,
  });

  const {
    data: embeddedConfig,
    isLoading: embeddedLoading,
    isError: embeddedError,
  } = useQuery({
    queryKey: ['servicenow-config', lockedCustomerId],
    enabled: embedded && !!lockedCustomerId,
    queryFn: async () => {
      try {
        const res = await api.get(`/integrations/servicenow/config/${lockedCustomerId}`);
        return res.data.data as ServiceNowConfig;
      } catch (err: any) {
        if (err.response?.status === 404) return null;
        throw err;
      }
    },
    refetchOnWindowFocus: false,
    staleTime: 30000,
  });

  const { data: customersData } = useQuery({
    queryKey: ['customers-list-servicenow'],
    enabled: !embedded,
    queryFn: async () => {
      const res = await api.get('/customers?limit=500');
      return (res.data.data || res.data.customers || []) as Customer[];
    },
    refetchOnWindowFocus: false,
  });

  const { data: engagementsData } = useQuery({
    queryKey: ['engagements-servicenow', form.customerId],
    enabled: !embedded && !!form.customerId,
    queryFn: async () => {
      const res = await api.get(`/engagements?customerId=${form.customerId}&limit=100`);
      return (res.data.data || res.data.engagements || []) as Engagement[];
    },
  });

  const invalidateConfigQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['servicenow-configs'] });
    if (lockedCustomerId) {
      queryClient.invalidateQueries({ queryKey: ['servicenow-config', lockedCustomerId] });
    }
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = { ...form };
      if (form.authType === 'oauth') {
        delete payload.authUsername;
        delete payload.password;
      } else {
        delete payload.clientId;
        delete payload.clientSecret;
      }
      if (editingCustomerId) {
        return api.put(`/integrations/servicenow/config/${editingCustomerId}`, payload);
      }
      return api.post('/integrations/servicenow/config', payload);
    },
    onSuccess: () => {
      invalidateConfigQueries();
      setShowModal(false);
      setEditingCustomerId(null);
      setForm(emptyForm);
      setError('');
      setMessage('Configuration saved successfully');
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to save configuration');
    },
  });

  const testMutation = useMutation({
    mutationFn: (customerId: string) =>
      api.post(`/integrations/servicenow/config/${customerId}/test`),
    onSuccess: () => setMessage('Connection test successful'),
    onError: (err: any) => setError(err.response?.data?.message || 'Connection test failed'),
  });

  const pollMutation = useMutation({
    mutationFn: (customerId: string) =>
      api.post(`/integrations/servicenow/config/${customerId}/poll`),
    onSuccess: () => setMessage('Manual poll triggered'),
    onError: (err: any) => setError(err.response?.data?.message || 'Poll failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: (customerId: string) =>
      api.delete(`/integrations/servicenow/config/${customerId}`),
    onSuccess: (_data, customerId) => {
      invalidateConfigQueries();
      setError('');
      setDeleteTarget(null);
      if (editingCustomerId === customerId) {
        setShowModal(false);
        setEditingCustomerId(null);
        setForm(emptyForm);
      }
      setMessage('Configuration deleted');
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to delete configuration');
    },
  });

  const openAddModal = () => {
    setEditingCustomerId(null);
    setMessage('');
    setError('');
    setForm({
      ...emptyForm,
      pollFromDate: todayDateInputValue(),
      ...(embedded && lockedCustomerId
        ? {
            customerId: lockedCustomerId,
            defaultEngagementId: lockedEngagementId || '',
          }
        : {}),
    });
    setShowModal(true);
  };

  const openEditModal = (cfg: ServiceNowConfig) => {
    setEditingCustomerId(cfg.customerId);
    setMessage('');
    setError('');
    setForm({
      customerId: cfg.customerId,
      isEnabled: cfg.isEnabled,
      instanceUrl: cfg.instanceUrl,
      authType: cfg.authType,
      authUsername: cfg.authUsername || '',
      password: '',
      clientId: '',
      clientSecret: '',
      serviceNowCompanySysId: cfg.serviceNowCompanySysId || '',
      defaultEngagementId: cfg.defaultEngagementId || (embedded ? lockedEngagementId || '' : ''),
      syncIncidents: cfg.syncIncidents,
      syncChanges: cfg.syncChanges,
      syncServiceRequests: cfg.syncServiceRequests,
      pollingEnabled: cfg.pollingEnabled,
      pollingIntervalMinutes: cfg.pollingIntervalMinutes,
      pollFromDate: toDateInputValue(cfg.pollFromDate),
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCustomerId(null);
    setForm(emptyForm);
    setMessage('');
    setError('');
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    setDeleting(true);
    deleteMutation.mutate(deleteTarget.customerId, {
      onSettled: () => setDeleting(false),
    });
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (form.authType === 'oauth') {
      if (!editingCustomerId && (!form.clientId.trim() || !form.clientSecret.trim())) {
        setError('Client ID and Client Secret are required for OAuth');
        return;
      }
    }
    if (form.isEnabled && !form.pollFromDate.trim()) {
      setError('Poll from date is required when integration is enabled');
      return;
    }
    saveMutation.mutate();
  };

  const ticketTypesLabel = (cfg: ServiceNowConfig) =>
    [cfg.syncIncidents && 'Incidents', cfg.syncChanges && 'Changes', cfg.syncServiceRequests && 'SR']
      .filter(Boolean)
      .join(', ') || '—';

  const isLoading = embedded ? embeddedLoading : listLoading;
  const isError = embedded ? embeddedError : listError;
  const configsForTable = configsData || [];
  const configuredEngagementDisplay = formatEngagementLabel(embeddedConfig?.Engagement);
  const canManageEmbeddedConfig =
    !embeddedConfig ||
    !embeddedConfig.defaultEngagementId ||
    embeddedConfig.defaultEngagementId === lockedEngagementId;
  const embeddedFormEngagementLabel = editingCustomerId
    ? configuredEngagementDisplay
    : engagementLabel || '—';
  const wrapperClass = embedded
    ? 'flex flex-col min-h-0'
    : 'p-4 flex flex-col h-full min-h-0 overflow-hidden';

  if (isLoading) {
    return (
      <div className={embedded ? '' : 'p-4'}>
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4" />
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-10 bg-gray-200 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={embedded ? '' : 'p-4'}>
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">Error loading ServiceNow configurations. Please try again.</p>
        </div>
      </div>
    );
  }

  const renderAlerts = () => (
    <>
      {message && (
        <div className="mb-3 flex-shrink-0 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-2 flex items-center justify-between">
          <span>{message}</span>
          <button type="button" onClick={() => setMessage('')} className="text-green-600 hover:text-green-800">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}
      {error && (
        <div className="mb-3 flex-shrink-0 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2 flex items-center justify-between">
          <span>{error}</span>
          <button type="button" onClick={() => setError('')} className="text-red-600 hover:text-red-800">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}
    </>
  );

  const renderFormFields = () => (
    <form onSubmit={handleSave} className="mt-4 space-y-4">
      {renderAlerts()}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Customer *</label>
          {embedded ? (
            <input
              className={`${integrationModalField} bg-gray-50`}
              value={customerName || form.customerId}
              disabled
              readOnly
            />
          ) : (
            <select
              className={integrationModalField}
              value={form.customerId}
              disabled={!!editingCustomerId}
              required
              onChange={(e) => setForm({ ...form, customerId: e.target.value, defaultEngagementId: '' })}
            >
              <option value="">Select customer</option>
              {(customersData || []).map((c) => (
                <option key={c.id} value={c.id}>{c.accountName}</option>
              ))}
            </select>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Engagement</label>
          {embedded ? (
            <input
              className={`${integrationModalField} bg-gray-50`}
              value={embeddedFormEngagementLabel}
              disabled
              readOnly
            />
          ) : (
            <select
              className={integrationModalField}
              value={form.defaultEngagementId}
              onChange={(e) => setForm({ ...form, defaultEngagementId: e.target.value })}
            >
              <option value="">Auto (first active)</option>
              {(engagementsData || []).map((e) => (
                <option key={e.id} value={e.id}>{e.number} - {e.name}</option>
              ))}
            </select>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Instance URL *</label>
          <input
            className={integrationModalField}
            value={form.instanceUrl}
            required
            onChange={(e) => setForm({ ...form, instanceUrl: e.target.value })}
            placeholder="https://customer.service-now.com"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Auth Type</label>
          <select
            className={integrationModalField}
            value={form.authType}
            onChange={(e) => setForm({ ...form, authType: e.target.value as 'basic' | 'oauth' })}
          >
            <option value="basic">Basic</option>
            <option value="oauth">OAuth (Client Credentials)</option>
          </select>
        </div>
        {form.authType === 'basic' ? (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                ServiceNow User ID (user_name)
              </label>
              <input
                className={integrationModalField}
                value={form.authUsername}
                onChange={(e) => setForm({ ...form, authUsername: e.target.value })}
                placeholder="e.g. pulse.integration"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Password</label>
              <input
                type="password"
                className={integrationModalField}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={editingCustomerId ? 'Leave blank to keep existing' : ''}
              />
            </div>
          </>
        ) : (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700">Client ID *</label>
              <input
                className={integrationModalField}
                value={form.clientId}
                required={!editingCustomerId}
                onChange={(e) => setForm({ ...form, clientId: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Client Secret *</label>
              <input
                type="password"
                className={integrationModalField}
                value={form.clientSecret}
                required={!editingCustomerId}
                onChange={(e) => setForm({ ...form, clientSecret: e.target.value })}
                placeholder={editingCustomerId ? 'Leave blank to keep existing' : ''}
              />
            </div>
          </>
        )}
      </div>

      <div className="flex flex-wrap gap-4 pt-2 border-t border-gray-100">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={form.isEnabled}
            onChange={(e) => setForm({ ...form, isEnabled: e.target.checked })}
          />
          Integration enabled
        </label>
        {(['syncIncidents', 'syncChanges', 'syncServiceRequests', 'pollingEnabled'] as const).map((key) => (
          <label key={key} className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form[key]}
              onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
            />
            {key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())}
          </label>
        ))}
      </div>

      <div className="pt-2 border-t border-gray-100">
        <label className="block text-sm font-medium text-gray-700">
          Poll from date {form.isEnabled ? '*' : ''}
        </label>
        <input
          type="date"
          className={`${integrationModalField} max-w-xs mt-1`}
          value={form.pollFromDate}
          required={form.isEnabled}
          onChange={(e) => setForm({ ...form, pollFromDate: e.target.value })}
        />
        <p className="mt-1 text-xs text-gray-500">
          Only ServiceNow records created on or after this date are imported via polling and webhooks.
          Changing this date resets the poll watermark.
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-gray-100">
        <div className="flex gap-2">
          {editingCustomerId && (
            <>
              <button
                type="button"
                className={integrationSecondaryButton}
                onClick={() => testMutation.mutate(editingCustomerId)}
                disabled={testMutation.isPending}
              >
                Test Connection
              </button>
              <button
                type="button"
                className={integrationSecondaryButton}
                onClick={() => pollMutation.mutate(editingCustomerId)}
                disabled={pollMutation.isPending}
              >
                Poll Now
              </button>
            </>
          )}
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={closeModal} className={integrationSecondaryButton}>
            Cancel
          </button>
          <button
            type="submit"
            className={integrationToolbarPrimaryButton}
            disabled={saveMutation.isPending || !form.customerId || !form.instanceUrl}
          >
            {saveMutation.isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </form>
  );

  const renderModals = () => (
    <>
      <Transition show={showModal} as={Fragment}>
        <Dialog onClose={closeModal} className="relative z-50">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30" />
          </Transition.Child>
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
                <Dialog.Title className="text-lg font-semibold text-gray-900">
                  {editingCustomerId ? 'Edit ServiceNow Configuration' : 'Add ServiceNow Configuration'}
                </Dialog.Title>
                {renderFormFields()}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      <Transition show={!!deleteTarget} as={Fragment}>
        <Dialog onClose={() => !deleting && setDeleteTarget(null)} className="relative z-50">
          <div className="fixed inset-0 bg-black/30" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
              <Dialog.Title className="text-lg font-semibold">Delete Configuration</Dialog.Title>
              <p className="mt-2 text-sm text-gray-600">
                Are you sure you want to delete the ServiceNow integration for &quot;
                {deleteTarget?.Customer?.accountName || customerName || deleteTarget?.customerId}&quot;?
                Sync links and logs will be kept for audit purposes.
              </p>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setDeleteTarget(null)}
                  disabled={deleting}
                  className={integrationSecondaryButton}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-1 focus:ring-red-500 disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      </Transition>
    </>
  );

  if (embedded) {
    return (
      <div className={wrapperClass}>
        <div className="bg-white shadow rounded-lg mb-3 flex-shrink-0">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">ServiceNow Integration</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Configuration for {customerName || 'this customer'}.
                  {embeddedConfig
                    ? ' Edit is available only on the engagement that owns this configuration.'
                    : ' Creating a configuration binds it to this engagement.'}
                </p>
              </div>
              {!embeddedConfig && canManageEmbeddedConfig && (
                <button
                  type="button"
                  onClick={openAddModal}
                  className={`${integrationToolbarPrimaryButton} !py-2 shrink-0`}
                >
                  <PlusIcon className="h-4 w-4 mr-1.5" />
                  Add Configuration
                </button>
              )}
            </div>
          </div>
        </div>

        {!showModal && renderAlerts()}

        <div className="bg-white shadow rounded-lg overflow-hidden">
          {!embeddedConfig ? (
            <div className="flex flex-col items-center justify-center p-8 text-center text-gray-500">
              <p className="text-sm">No ServiceNow configuration for this customer yet.</p>
              <button type="button" onClick={openAddModal} className={`${integrationToolbarPrimaryButton} mt-4`}>
                <PlusIcon className="h-4 w-4 mr-1.5" />
                Add Configuration
              </button>
            </div>
          ) : (
            <div className="p-4 space-y-4">
              {!canManageEmbeddedConfig && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  This configuration is managed from engagement{' '}
                  <span className="font-medium">{configuredEngagementDisplay}</span>.
                  Open that engagement&apos;s Misc tab to edit or delete it.
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                <div>
                  <dt className="text-gray-500">Customer</dt>
                  <dd className="mt-0.5 font-medium text-gray-900">
                    {embeddedConfig.Customer?.accountName || customerName || embeddedConfig.customerId}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Engagement</dt>
                  <dd className="mt-0.5 font-medium text-gray-900">{configuredEngagementDisplay}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Instance URL</dt>
                  <dd className="mt-0.5 font-medium text-gray-900 break-all">{embeddedConfig.instanceUrl}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Status</dt>
                  <dd className="mt-0.5">
                    <span
                      className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                        embeddedConfig.isEnabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {embeddedConfig.isEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Auth</dt>
                  <dd className="mt-0.5 font-medium text-gray-900 uppercase">{embeddedConfig.authType}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Ticket Types</dt>
                  <dd className="mt-0.5 font-medium text-gray-900">{ticketTypesLabel(embeddedConfig)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Poll From</dt>
                  <dd className="mt-0.5 font-medium text-gray-900">
                    {formatPollFromDateDisplay(embeddedConfig.pollFromDate)}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Polling</dt>
                  <dd className="mt-0.5 font-medium text-gray-900">
                    {embeddedConfig.pollingEnabled ? 'Yes' : 'No'}
                  </dd>
                </div>
              </div>
              {canManageEmbeddedConfig && (
                <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => openEditModal(embeddedConfig)}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-indigo-700 bg-indigo-50 rounded-md hover:bg-indigo-100"
                  >
                    <PencilSquareIcon className="h-4 w-4 mr-1.5" />
                    Edit
                  </button>
                  <button
                    type="button"
                    className={integrationSecondaryButton}
                    onClick={() => testMutation.mutate(embeddedConfig.customerId)}
                    disabled={testMutation.isPending}
                  >
                    Test Connection
                  </button>
                  <button
                    type="button"
                    className={integrationSecondaryButton}
                    onClick={() => pollMutation.mutate(embeddedConfig.customerId)}
                    disabled={pollMutation.isPending}
                  >
                    Poll Now
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteTarget(embeddedConfig)}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 rounded-md hover:bg-red-100"
                  >
                    <TrashIcon className="h-4 w-4 mr-1.5" />
                    Delete
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {renderModals()}
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      <div className="bg-white shadow rounded-lg mb-3 flex-shrink-0">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h1 className="text-xl font-bold text-gray-900">ServiceNow Integration</h1>
              <p className="text-sm text-gray-500 mt-1">
                Bi-directional sync for Incidents, Change Requests, and Service Requests only.
                Only ServiceNow records assigned to the <strong>Infrastructure Support</strong> assignment group
                are imported into Pulse (webhooks and polling). For Service Catalog requests, Assignment Group
                is checked on the related <strong>Catalog Task</strong> (not on Request / Request Item).
                New ServiceNow-created Incidents, Changes, and Service Requests appear in the header bell
                for all agent users until the first Pulse activity is logged on that ticket.
                If ServiceNow Assignment Group leaves <strong>Infrastructure Support</strong>, linked Incidents/SRs
                are set to Resolved in Pulse only (ServiceNow state is not changed); after the usual 3-day
                auto-close they become Closed in Pulse without closing ServiceNow. Linked Change Requests
                are Closed in Pulse immediately (ServiceNow Change state is not changed). Returning to Tenarai
                reopens Resolved Incidents/SRs from ServiceNow state; Closed Pulse tickets (including Changes
                closed by leaving Tenarai) are never reopened — a new Pulse ticket is created and linked instead.
                Changing Assignment Group in Pulse updates the ServiceNow activity log only — the ServiceNow
                Assignment Group stays <strong>Infrastructure Support</strong>.
                Changing Assigned To in Pulse updates ServiceNow when the user exists there; otherwise the
                name is written to the ServiceNow activity log and Assigned To stays blank.
                Creating or updating these records in Pulse creates or updates the linked ServiceNow record.
                Cases, Problems, and Alerts are not synced with ServiceNow. Webhook:{' '}
                <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">
                  POST /api/v1/integrations/servicenow/webhook
                </code>
                . ServiceNow attachments require a separate business rule on{' '}
                <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">sys_attachment</code>{' '}
                (or use Poll Now / scheduled polling to pull attachments for linked tickets).
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Link
                to="/admin/servicenow-logs"
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <ClipboardDocumentListIcon className="h-4 w-4 mr-2" />
                View Logs
              </Link>
              <button
                type="button"
                onClick={openAddModal}
                className={`${integrationToolbarPrimaryButton} !py-2`}
              >
                <PlusIcon className="h-4 w-4 mr-1.5" />
                Add Configuration
              </button>
            </div>
          </div>
        </div>
      </div>

      {!showModal && renderAlerts()}

      <div className="bg-white shadow rounded-lg flex flex-col flex-1 min-h-0 overflow-hidden">
        {configsForTable.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center text-gray-500">
            <p className="text-sm">No ServiceNow configurations yet.</p>
            <button type="button" onClick={openAddModal} className={`${integrationToolbarPrimaryButton} mt-4`}>
              <PlusIcon className="h-4 w-4 mr-1.5" />
              Add Configuration
            </button>
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50">Customer</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50">Instance URL</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50">Status</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50">Auth</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50">Ticket Types</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50">Poll From</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase bg-gray-50">Polling</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase bg-gray-50">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {configsForTable.map((cfg) => (
                  <tr key={cfg.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {cfg.Customer?.accountName || cfg.customerId}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate" title={cfg.instanceUrl}>
                      {cfg.instanceUrl}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                          cfg.isEnabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {cfg.isEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 uppercase">{cfg.authType}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{ticketTypesLabel(cfg)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatPollFromDateDisplay(cfg.pollFromDate)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{cfg.pollingEnabled ? 'Yes' : 'No'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => openEditModal(cfg)}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 rounded hover:bg-indigo-100"
                          title="Edit"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(cfg)}
                          disabled={deleting && deleteTarget?.customerId === cfg.customerId}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-700 bg-red-50 rounded hover:bg-red-100 disabled:opacity-50"
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {renderModals()}
    </div>
  );
};

export default ServiceNowIntegrationConfig;
